import { CardProps } from '@chakra-ui/react';
import React, { useCallback, useMemo } from 'react';
import { Card } from 'ui-components';
import { SavedList } from '~/clientApi/savedLists.model';
import { NoteBlock } from '~/components/NoteBlock';
import { getMainPlatformData } from '~/core/utils';
import { ICreator } from '~/types';

import { useListsStore } from '../store/useSavedListStore';
import { CreatorInfo } from './CreatorInfo';

export interface StackViewCreatorProps {
  list: SavedList;
  creator: ICreator;
  isChecked?: boolean;
  isCheckEnabled?: boolean;
  isShared?: boolean;
  note?: string;
  onNoteOpen?: () => void;
}

//Copied from src/screens/MyAgency/components/StackViewCreator.tsx
export function StackViewCreator({
  list,
  creator,
  isChecked,
  isCheckEnabled,
  isShared,
  note,
  onNoteOpen,
}: StackViewCreatorProps) {
  const { toggleCreator } = useListsStore();

  const { name = '' } = getMainPlatformData(creator);

  const handleSelectCreator = useCallback(
    (isSelected: boolean) => {
      toggleCreator(creator, isSelected);
    },
    [creator, toggleCreator],
  );

  const cardStyleProps: CardProps = useMemo(() => {
    if (isChecked) {
      const checkedCardStyles = {
        borderStyle: 'solid',
        borderWidth: '1px',
        borderColor: 'secondary.500',
        backgroundColor: '#FFFAF5',
      };

      return checkedCardStyles;
    }

    const commonStyles = {
      _hover: {
        cursor: 'initial',
      },
    };

    return isShared
      ? {
          ...commonStyles,
          _active: {
            backgroundColor: 'natural.100',
          },
        }
      : {
          ...commonStyles,
        };
  }, [isShared, isChecked]);

  return (
    <Card
      key={creator.id}
      boxShadow="0"
      border="1px solid"
      borderBottomWidth={{ base: '0', sm: '1px' }}
      borderBottomStyle={{ base: 'none', sm: 'solid' }}
      borderBottomColor={{
        base: 'transparent',
        sm: isChecked ? 'secondary.500' : 'gray.200',
      }}
      borderColor="primary.100"
      marginBottom={{
        base: 0,
        sm: 5,
      }}
      borderRadius={{ base: 0, sm: 'radius-150' }}
      cursor="pointer"
      data-testid={name}
      {...cardStyleProps}
    >
      <CreatorInfo
        list={list}
        creator={creator}
        note={note}
        isShared={isShared}
        onCheck={isCheckEnabled ? handleSelectCreator : undefined}
        onNoteOpen={onNoteOpen}
      />

      {note && <NoteBlock note={note} marginX={6} marginTop={4} />}
    </Card>
  );
}
